/*
 * Round 14 (2026-09-28) - ONE switch for every V3 change of this round:
 *
 *   - slot settings render server-only settings from the V1 metadata endpoint (the welcome DM
 *     per-hour/per-day limits) - see lib/serverSettings.js;
 *   - the Target Data page renders the settings the server defines for it ("Pick targets in order
 *     (one per session)", "Auto-remove not-found targets from the list" - slot settings 126 / 127
 *     until they moved there before round 14 shipped) from GET /uiapi/target-data/metadata, and
 *     the not-found targets list with Restore - pages/SlotTargetDataPage.js, lib/targetData.js;
 *   - the Slots table shows Story views and Story likes as their own columns - lib/storyColumns.js;
 *   - the Slot Stats page shows the 90-day histories, growth, ratios and the not-found targets
 *     list with Restore - pages/SlotStatsPage.js, components/NotFoundTargetsCard.js.
 *
 * Default ON. OFF gives the round-13 UI exactly (the same components, the same requests).
 *
 *   build time: REACT_APP_NECRO_UI_R14=off   (also 0 / false / no) in the build environment
 *   run time:   localStorage['necro.ui.r14'] = 'off' in one browser, then reload - a per-viewer
 *               escape hatch that needs no rebuild; 'on' there overrides a build-time off.
 */

const OFF_WORDS = new Set(['off', '0', 'false', 'no']);
const ON_WORDS = new Set(['on', '1', 'true', 'yes']);

const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

/**
 * Pure decision, exported (as resolveR14 / resolveR14b) for tests.
 * @param envValue      the build-time value (process.env.REACT_APP_NECRO_UI_R14)
 * @param storageValue  the browser's localStorage['necro.ui.r14'] (null when unset/unreadable)
 * @returns {{ enabled: boolean, source: string }}
 */
function resolveSwitch(storageKey, envName, envValue, storageValue) {
  const s = norm(storageValue);
  if (OFF_WORDS.has(s)) return { enabled: false, source: `localStorage ${storageKey}=` + s };
  if (ON_WORDS.has(s)) return { enabled: true, source: `localStorage ${storageKey}=` + s };
  const e = norm(envValue);
  if (OFF_WORDS.has(e)) return { enabled: false, source: `build ${envName}=` + e };
  return { enabled: true, source: e ? `build ${envName}=` + e : 'default' };
}

export function resolveR14(envValue, storageValue) {
  return resolveSwitch('necro.ui.r14', 'REACT_APP_NECRO_UI_R14', envValue, storageValue);
}

function readStorage(key) {
  // Private windows, blocked site data and thumbnail renderers throw on the accessor itself.
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

let logged = false;

/** true unless round 14 was switched off (see the header). Logs its decision once per page load. */
export function r14UiEnabled() {
  const r = resolveR14(process.env.REACT_APP_NECRO_UI_R14, readStorage('necro.ui.r14'));
  if (!logged) {
    logged = true;
    // eslint-disable-next-line no-console
    console.info(`[R14-UI] round-14 UI ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * Round 14b (2026-09-29) - the not-found targets list BY TARGET TYPE (fix W11, contract C2): the
 * worker now counts a miss per list type + target (a hashtag 'nike' that is never found no longer
 * skips the competitor account 'nike'), and the server keeps one row per target + type. The card
 * (components/NotFoundTargetsCard.js, on Slot Stats and on the Target Data page) then:
 *   - shows each row's type as a label (Competitor account / Hashtag / Location / Direct account;
 *     no label when the server sent no type or one this UI does not know),
 *   - keys its rows by target + type (the same target can be skipped in two lists),
 *   - restores with POST {target, type} (just {target} when the row has no type - the server then
 *     restores every type of that target, which is all the round-14 server can do).
 *
 * Default ON. OFF gives the deployed round-14 card exactly (no label, rows keyed by target, restore
 * sends {target} only). Against a server that sends no type the two are the same. Round 14 OFF
 * hides the card altogether, so this switch only matters while round 14 is ON.
 *
 *   build time: REACT_APP_NECRO_UI_R14B=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r14b'] = 'off' in one browser, then reload; 'on' there
 *               overrides a build-time off.
 */
export function resolveR14b(envValue, storageValue) {
  return resolveSwitch('necro.ui.r14b', 'REACT_APP_NECRO_UI_R14B', envValue, storageValue);
}

let loggedB = false;

/** true unless round 14b was switched off (see above). Logs its decision once per page load. */
export function r14bUiEnabled() {
  const r = resolveR14b(process.env.REACT_APP_NECRO_UI_R14B, readStorage('necro.ui.r14b'));
  if (!loggedB) {
    loggedB = true;
    // eslint-disable-next-line no-console
    console.info(`[R14-UI] round-14b UI (not-found targets by type) ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * Round 15 (2026-09-30) - the two Slot Stats cards of round 15 (components/stats/CloseFriendsCard.js,
 * components/stats/SavedCollectionCard.js): Close Friends (Varun item 6, settings 716-722, GET
 * /uiapi/slots/{slot}/close-friends) and repost from the saved collection (Varun item 7, settings 697-700,
 * GET /uiapi/slots/{slot}/saved-collection). Each card is drawn only when the server answers its route; a
 * server without it (404 / 405, or its part switched off: 404 not_enabled) shows nothing, exactly as now.
 *
 * Default ON. OFF = neither card is requested or drawn (the round-14 page). The slot settings of round 15
 * need no switch: they are the server's (GET /uiapi/settings/metadata lists them only while their part is
 * ON), and "Close Friends" is only a section name the page now knows how to place.
 *
 *   build time: REACT_APP_NECRO_UI_R15=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r15'] = 'off' in one browser, then reload; 'on' there
 *               overrides a build-time off.
 */
export function resolveR15(envValue, storageValue) {
  return resolveSwitch('necro.ui.r15', 'REACT_APP_NECRO_UI_R15', envValue, storageValue);
}

let logged15 = false;

/** true unless round 15 was switched off (see above). Logs its decision once per page load. */
export function r15UiEnabled() {
  const r = resolveR15(process.env.REACT_APP_NECRO_UI_R15, readStorage('necro.ui.r15'));
  if (!logged15) {
    logged15 = true;
    // eslint-disable-next-line no-console
    console.info(`[R15-UI] round-15 Slot Stats cards (Close Friends, saved collection) ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * Round 16m (2026-10-20) - follow-back attribution in V3 (design D:/NecroWork/r16m-batch-evidence/DESIGN.md 3.7): the Follow-back
 * card on Slot Stats (components/stats/FollowBackCard.js), the /follow-back page with its "Follow-back" nav item and batch panel
 * (pages/FollowBackPage.js), and the live #1217 hint card on the slot settings page (lib/batch1217Warning.js). Each draws only
 * from the server's r16m answers: a server without the routes shows nothing (one console line).
 *
 * Default ON (features ship on). OFF = none of the three is requested or drawn, and /follow-back goes to the dashboard: the page
 * exactly as before r16m.
 *
 *   build time: REACT_APP_NECRO_UI_R16M=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r16m'] = 'off' in one browser, then reload; 'on' there overrides a build-time off.
 */
export function resolveR16m(envValue, storageValue) {
  return resolveSwitch('necro.ui.r16m', 'REACT_APP_NECRO_UI_R16M', envValue, storageValue);
}

let logged16m = false;

/** true unless round 16m was switched off (see above). Logs its decision once per page load. */
export function r16mUiEnabled() {
  const r = resolveR16m(process.env.REACT_APP_NECRO_UI_R16M, readStorage('necro.ui.r16m'));
  if (!logged16m) {
    logged16m = true;
    // eslint-disable-next-line no-console
    console.info(`[R16M-UI] round-16m follow-back card, page and #1217 hint ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * Round 16n (2026-10-21) - story shares to Close Friends in V3 (design D:/NecroWork/r15k10-cf-evidence/DESIGN.md 2 and 4): the
 * "Story shares" card on Slot Stats (components/stats/StoryShareCard.js: Your story vs Close Friends, from the server's r16n /stats
 * fields) and the live #1109 / #1110 hint on the slot settings page (lib/storyCloseFriends.js). The setting pair itself is V1's and
 * is drawn whatever this switch says (the server's metadata and values decide, as for every setting).
 *
 * Default ON (features ship on). OFF = neither the card nor the hint is drawn: the pages exactly as before r16n, plus the pair row.
 *
 *   build time: REACT_APP_NECRO_UI_R16N=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r16n'] = 'off' in one browser, then reload; 'on' there overrides a build-time off.
 */
export function resolveR16n(envValue, storageValue) {
  return resolveSwitch('necro.ui.r16n', 'REACT_APP_NECRO_UI_R16N', envValue, storageValue);
}

let logged16n = false;

/** true unless round 16n was switched off (see above). Logs its decision once per page load. */
export function r16nUiEnabled() {
  const r = resolveR16n(process.env.REACT_APP_NECRO_UI_R16N, readStorage('necro.ui.r16n'));
  if (!logged16n) {
    logged16n = true;
    // eslint-disable-next-line no-console
    console.info(`[R16N-UI] round-16n Story shares card and #1109/#1110 hint ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * r16o3 v3-backlog (2026-10-24) - the owner's future todos 10, 4 and 2 in V3:
 *   - the sign-in page tells "the server did not answer" (no response, or a gateway / dev-proxy 502 / 503 / 504) apart from a
 *     real 401 "check your email and password" (lib/loginError.js; item 10);
 *   - SMM > Subscriptions (/smm-subscriptions): the legacy /smmSubscriptionOrders/ page with pause / resume (item 4,
 *     pages/SmmSubscriptionsPage.js, server r16o3 GET /uiapi/smm-subscription-orders);
 *   - Growth > Inbuilt scraping (/inbuilt-scraping): the inbuilt-scraping block of the legacy /orderManagement/accountSettings
 *     (item 2, pages/InbuiltScrapingPage.js, server r16o3 /uiapi/inbuilt-scraping).
 * A page whose server route is missing (a server before r16o3: 404 / 405) says so and offers the legacy page instead.
 *
 * Default ON (features ship on). OFF = the sign-in message exactly as before, no new menu entry, both routes go to the dashboard.
 *
 *   build time: REACT_APP_NECRO_UI_R16O3=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r16o3'] = 'off' in one browser, then reload; 'on' there overrides a build-time off.
 */
export function resolveR16o3(envValue, storageValue) {
  return resolveSwitch('necro.ui.r16o3', 'REACT_APP_NECRO_UI_R16O3', envValue, storageValue);
}

let logged16o3 = false;

/** true unless r16o3 was switched off (see above). Logs its decision once per page load. */
export function r16o3UiEnabled() {
  const r = resolveR16o3(process.env.REACT_APP_NECRO_UI_R16O3, readStorage('necro.ui.r16o3'));
  if (!logged16o3) {
    logged16o3 = true;
    // eslint-disable-next-line no-console
    console.info(`[R16O3-UI] r16o3 sign-in errors, Subscriptions and Inbuilt scraping pages ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}

/*
 * Round 16p (2026-10-24) - the DM chats round in V3 (design D:/NecroWork/dm-chats-evidence/DESIGN.md section 7): the Inbox page at
 * /inbox (and /slots/:slotName/inbox), the "Inbox" nav item with its unread pill, the Unread column and the Inbox link on the Slots
 * page, the 776 hint on the slot settings page, and /settings/api (API keys, Lock API access, Docs). Each draws only when its route
 * answers (lib/chatsView.js classifyChatsError): a server without the r16p routes shows exactly r16n plus one console line. The
 * "DM Inbox (Chats)" slot settings themselves are the server's (GET /uiapi/settings/metadata) and are drawn whatever this switch says.
 *
 * Default ON (features ship on). OFF = none of it is requested or drawn, /inbox and /settings/api go to the dashboard: r16n, except
 * three pieces that stay whatever this switch says (review 2026-10-24: "exactly r16n" was not literally true), all inert against an
 * r16n server:
 *   - "DM Inbox (Chats)" in KNOWN_SECTIONS / SECTION_ORDER / MAIN_CATEGORIES: a section NAME only, used only when a server sends
 *     settings in it (an r16n server sends none);
 *   - #772 option 3 (automatic AI replies) is dropped from any server select (lib/serverSettings.js allowedServerOptions): a safety
 *     rule of O1, never switched off;
 *   - this function's one "[R16P-UI] ... OFF" console line (every round switch logs its decision once).
 *
 *   build time: REACT_APP_NECRO_UI_R16P=off   (also 0 / false / no)
 *   run time:   localStorage['necro.ui.r16p'] = 'off' in one browser, then reload; 'on' there overrides a build-time off.
 */
export function resolveR16p(envValue, storageValue) {
  return resolveSwitch('necro.ui.r16p', 'REACT_APP_NECRO_UI_R16P', envValue, storageValue);
}

let logged16p = false;

/** true unless round 16p was switched off (see above). Logs its decision once per page load. */
export function r16pUiEnabled() {
  const r = resolveR16p(process.env.REACT_APP_NECRO_UI_R16P, readStorage('necro.ui.r16p'));
  if (!logged16p) {
    logged16p = true;
    // eslint-disable-next-line no-console
    console.info(`[R16P-UI] round-16p Inbox, unread column and API access ${r.enabled ? 'ON' : 'OFF'} (${r.source})`);
  }
  return r.enabled;
}
